import { useState } from "react";
import { RotateCcw, Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Buyer logins are parked, so a returning buyer gets their last order back by
   typing the mobile number they ordered with. */
export const ReorderPanel = ({ onLoad }) => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const lookup = async () => {
    setBusy(true);
    setMessage("");
    try {
      const res = await fetch(`${API}/order-enquiries/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body.detail, "Could not look that up."));
      const count = onLoad(body.items || []);
      if (count === null) {
        setMessage("");
        return;
      }
      setMessage(`Loaded ${count} line${count === 1 ? "" : "s"} from ${body.reference}.`);
    } catch (e) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="reorder-open"
        className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
        style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
      >
        <RotateCcw className="w-3.5 h-3.5" /> Order again
      </button>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-sm border w-full"
      style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
      data-testid="reorder-panel"
    >
      <input
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && !busy && lookup()}
        inputMode="tel"
        placeholder="Mobile you ordered with"
        data-testid="reorder-phone-input"
        className="flex-1 min-w-[10rem] text-sm bg-transparent outline-none"
        style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
      />
      <button
        onClick={lookup}
        disabled={busy}
        data-testid="reorder-submit"
        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-sm transition-opacity hover:opacity-90 disabled:opacity-60"
        style={{
          backgroundColor: "var(--mp-copper-solid)",
          color: "#FFFFFF",
          fontFamily: "'Manrope', sans-serif",
        }}
      >
        {busy && <Loader2 className="w-3 h-3 animate-spin" />}
        Load
      </button>
      {message && (
        <p
          className="w-full text-[11px]"
          style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          data-testid="reorder-message"
        >
          {message}
        </p>
      )}
    </div>
  );
};
